import { cancelStatementDefaultsApi } from "@/dashboard/inventory_management/cancel/cancelStatementDefaultsApi";
import { requiresTgeOptions } from "@/dashboard/inventory_management/common/cancellationRegistryRules";
import type { TgeLegalForm } from "@/types";
import { apiErrorLogFields } from "@/utils/apiErrors";
import { debounce } from "lodash-es";
import { type Ref, onBeforeUnmount, watch } from "vue";

const LOOKUP_DEBOUNCE_MS = 300;

/**
 * @property tge_legal_form_detected - What the backend read from the name: undefined until a lookup
 * answers, null when the name carries no legal-form designation.
 * @property tge_detection_key - The name and region the current detection answers; owned by the
 * composable.
 */
export interface TgeDetectableRow {
  beneficiary_name: string;
  registry_country_region: string;
  tge_legal_form?: TgeLegalForm | null;
  tge_legal_form_detected?: TgeLegalForm | null;
  tge_detection_key?: string;
}

/**
 * Prefills each TGE (Poland) row's legal form from what the backend reads off its beneficiary name,
 * looking it up again whenever the name or region changes. A legal form the user picked by hand is
 * kept: the detection only replaces an empty choice or its own previous answer. A failed lookup
 * drops the previous detection, so the row asks for a choice instead of submitting a stale one.
 */
export const useTgeLegalFormDetection = (options: {
  rows: Ref<TgeDetectableRow[]>;
  companyId: () => number;
  enabled: () => boolean;
}) => {
  const lookUp = async (row: TgeDetectableRow) => {
    const key = lookupKey(row);
    row.tge_detection_key = key;
    try {
      const response = await cancelStatementDefaultsApi.get(
        options.companyId(),
        row.beneficiary_name.trim(),
        row.registry_country_region
      );
      if (lookupKey(row) !== key) {
        forgetLookup(row, key);
        return;
      }
      applyDetection(row, response.data.tge_options?.legal_form ?? null);
    } catch (error) {
      const isCurrent = lookupKey(row) === key;
      forgetLookup(row, key);
      if (isCurrent) clearDetection(row);
      console.warn(
        "Failed to read the legal form from the beneficiary name",
        apiErrorLogFields(error)
      );
    }
  };

  const syncAll = async () => {
    if (!options.enabled()) return;
    await Promise.all(
      options.rows.value
        .filter(
          (row) =>
            requiresTgeOptions(row.registry_country_region) &&
            row.beneficiary_name.trim() &&
            row.tge_detection_key !== lookupKey(row)
        )
        .map(lookUp)
    );
  };

  const debouncedSync = debounce(syncAll, LOOKUP_DEBOUNCE_MS);

  watch(() => options.rows.value.map(lookupKey), debouncedSync, {
    immediate: true,
  });

  onBeforeUnmount(debouncedSync.cancel);
};

const lookupKey = (row: TgeDetectableRow) =>
  `${row.registry_country_region}\u0000${row.beneficiary_name.trim()}`;

const forgetLookup = (row: TgeDetectableRow, key: string) => {
  if (row.tge_detection_key === key) {
    row.tge_detection_key = undefined;
  }
};

const applyDetection = (
  row: TgeDetectableRow,
  detected: TgeLegalForm | null
) => {
  const previousDetection = row.tge_legal_form_detected ?? null;
  row.tge_legal_form_detected = detected;
  if (!row.tge_legal_form || row.tge_legal_form === previousDetection) {
    row.tge_legal_form = detected;
  }
};

const clearDetection = (row: TgeDetectableRow) => {
  const previousDetection = row.tge_legal_form_detected ?? null;
  row.tge_legal_form_detected = undefined;
  if (row.tge_legal_form === previousDetection) {
    row.tge_legal_form = null;
  }
};
